import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import { acceptInvitation, validateInvitation } from "../../api/invitationApi";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const email = useMemo(() => (searchParams.get("email") || "").trim(), [searchParams]);
  const token = useMemo(() => (searchParams.get("token") || "").trim(), [searchParams]);
  const [validationState, setValidationState] = useState("validating");
  const [validationMessage, setValidationMessage] = useState("");
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (!email || !token) {
      setValidationState("invalid");
      setValidationMessage("This invitation link is incomplete or invalid.");
      return () => {
        isMounted = false;
      };
    }

    setValidationState("validating");
    validateInvitation(email, token)
      .then(() => {
        if (isMounted) setValidationState("valid");
      })
      .catch((error) => {
        if (!isMounted) return;
        const status = error?.response?.status;
        setValidationState(!status || status >= 500 ? "error" : "invalid");
        setValidationMessage(
          getErrorMessage(error, "This invitation is invalid or has expired.")
        );
      });

    return () => {
      isMounted = false;
    };
  }, [email, token, validationAttempt]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    if (password.length < 8) {
      setFormError("Your password must contain at least 8 characters.");
      return;
    }
    if (password !== passwordConfirmation) {
      setFormError("The passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      await acceptInvitation(email, token, password);
      setValidationState("accepted");
      setPassword("");
      setPasswordConfirmation("");
    } catch (error) {
      setFormError(
        getErrorMessage(error, "Your password could not be set. Please try again.")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        position: "relative",
        overflow: "hidden",
        px: 2,
        py: 5,
        background:
          "radial-gradient(circle at 10% 15%, rgba(110,185,110,.22), transparent 34%), linear-gradient(145deg, #f7faf7 0%, #eef4ef 52%, #e4eee6 100%)",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          width: 420,
          height: 420,
          border: "1px solid rgba(110,185,110,.28)",
          borderRadius: "50%",
          right: -190,
          bottom: -210,
        }}
      />
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 480,
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          border: "1px solid rgba(52,52,52,.09)",
          boxShadow: "0 24px 70px rgba(31, 55, 35, .13)",
          backgroundColor: "rgba(255,255,255,.94)",
          backdropFilter: "blur(12px)",
        }}
      >
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              component="img"
              src="/assets/svg/logo.svg"
              alt="Ziggurat"
              sx={{ width: 44, height: 44 }}
            />
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 18, letterSpacing: ".13em" }}>
                ZIGGURAT
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 11, letterSpacing: ".08em" }}>
                SMART SEWER SYSTEM PLANNING
              </Typography>
            </Box>
          </Stack>

          {validationState === "validating" && (
            <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
              <CircularProgress color="secondary" size={34} />
              <Typography color="text.secondary">Checking your invitation…</Typography>
            </Stack>
          )}

          {validationState === "invalid" && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h3" component="h1" sx={{ mb: 1 }}>
                  Invitation unavailable
                </Typography>
                <Typography color="text.secondary">
                  The link may have expired or already been used.
                </Typography>
              </Box>
              <Alert severity="error">{validationMessage}</Alert>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Ask the person who invited you to send a new invitation.
              </Typography>
            </Stack>
          )}

          {validationState === "error" && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h3" component="h1" sx={{ mb: 1 }}>
                  We couldn't check your invitation
                </Typography>
                <Typography color="text.secondary">
                  The service is temporarily unavailable. Your invitation link has not been used.
                </Typography>
              </Box>
              <Alert severity="warning">{validationMessage}</Alert>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setValidationAttempt((current) => current + 1)}
              >
                Try again
              </Button>
            </Stack>
          )}

          {validationState === "valid" && (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h3" component="h1" sx={{ mb: 1 }}>
                    Create your password
                  </Typography>
                  <Typography color="text.secondary">
                    Complete your Ziggurat account for <strong>{email}</strong>.
                  </Typography>
                </Box>

                {formError && <Alert severity="error">{formError}</Alert>}

                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                  required
                  fullWidth
                  helperText="At least 8 characters"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowPassword((current) => !current)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Confirm password"
                  type={showPassword ? "text" : "password"}
                  value={passwordConfirmation}
                  onChange={(event) => setPasswordConfirmation(event.target.value)}
                  autoComplete="new-password"
                  required
                  fullWidth
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="large"
                  disabled={isSubmitting}
                  sx={{ minHeight: 48, fontWeight: 600 }}
                >
                  {isSubmitting ? <CircularProgress size={22} color="inherit" /> : "Set password"}
                </Button>
              </Stack>
            </Box>
          )}

          {validationState === "accepted" && (
            <Stack spacing={2.5} alignItems="flex-start">
              <CheckCircleRoundedIcon color="secondary" sx={{ fontSize: 52 }} />
              <Box>
                <Typography variant="h3" component="h1" sx={{ mb: 1 }}>
                  Your account is ready
                </Typography>
                <Typography color="text.secondary">
                  Your password has been saved and you have joined the workspace.
                </Typography>
              </Box>
              <Button
                component={Link}
                to="/"
                variant="contained"
                color="secondary"
                size="large"
                fullWidth
                sx={{ minHeight: 48, fontWeight: 600 }}
              >
                Continue to login
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default SetPassword;
